import { ReactElement, useState } from 'react';
import OtpInput from 'react-otp-input';
import { connect } from 'react-redux';
import { CopyToClipboard } from 'react-copy-to-clipboard';
import { UserSettingService } from 'services/UserSettingService';
import { Utils } from 'shared/helpers';
import {
    Box,
    Dialog,
    DialogActions,
    IconButton,
    Typography,
} from '@mui/material';
import {
    AppPrimaryButtonOutlined,
    AppPrimaryButton,
} from 'shared/components/AppComponents';
import { ProfileActions } from 'state/profile/Action';
import CloseIcon from '@mui/icons-material/Close';

const TwoFactorConfirmationDialog = (props: any): ReactElement => {
    const {
        showForm,
        onCloseForm,
        profileState,
        twoFASetupData,
        update2FAStatus,
    } = props;
    const [copied, setCopied] = useState(false);
    const [otp, setOtp] = useState('');
    const [loader, setLoader] = useState(false);

    // API Calls
    const confirmTwoFASetup = (formValues: any, callback: any): void => {
        setLoader(true);
        UserSettingService.confirmTwoFactor(formValues)
            .then((response) => {
                Utils.handleSuccessResponse(response);
                setLoader(false);
                callback(true);
                onCloseForm();
            })
            .catch((error) => {
                Utils.handleErrorResponse(error);
                setLoader(false);
                callback(false);
            });
    };

    // UI Actions
    const actionCompleteTwoFaSetup = (): void => {
        const formValues = { code: otp };
        confirmTwoFASetup(formValues, (status: any) => {
            if (status) {
                profileState.userInfo.twoFactorAuthentication.enabled = true;
                profileState.userInfo.twoFactorAuthentication.setupCompleted =
                    true;
                update2FAStatus(profileState);
            }
        });
    };

    const pinOnChange = (value: any): void => setOtp(value);

    // UI Elements

    return (
        <Dialog
            fullWidth
            maxWidth="sm"
            open={showForm}
            className="twofactor-wrapper"
        >
            <Box className="header">
                <Typography variant="h3">Two factor code</Typography>
                <IconButton
                    edge="end"
                    onClick={onCloseForm}
                    className="app-icon-close"
                    title="close"
                >
                    <CloseIcon className="close-icon" />
                </IconButton>
            </Box>

            <Box className="content">
                <Typography variant="body1" className="description">
                    Use Google authenticator to get your code. Without this code
                    you cannot login to your account.
                </Typography>
                <Box className="my-2 justify-center">
                    <img src={twoFASetupData.url} alt="qrcode" width="150" />
                </Box>
                <Box className="justify-space-between">
                    <Typography variant="body1" className="description">
                        Authenticator code:
                    </Typography>

                    <CopyToClipboard
                        text={
                            twoFASetupData?.secret ? twoFASetupData?.secret : ''
                        }
                        onCopy={() => setCopied(!copied)}
                    >
                        {copied === false ? (
                            <AppPrimaryButtonOutlined
                                className="btn-small"
                                title="Copy"
                            />
                        ) : (
                            <AppPrimaryButtonOutlined
                                className="btn-small"
                                title="Copied"
                            />
                        )}
                    </CopyToClipboard>
                </Box>

                <Typography
                    variant="body1"
                    className="tfa-secret mt-4 lato-bold"
                >
                    {twoFASetupData.secret}
                </Typography>

                <Typography variant="body1" className="mt-5 description">
                    Verification code:
                </Typography>
                <Box className="my-4 justify-center">
                    <OtpInput
                        value={otp}
                        onChange={pinOnChange}
                        numInputs={6}
                        inputStyle="otp-input"
                        isInputNum
                        isInputSecure
                        shouldAutoFocus
                        separator={<span className="otp-split" />}
                    />
                </Box>
            </Box>

            <DialogActions className="px-6">
                <AppPrimaryButton
                    className="btn-small mb-4"
                    title="Submit"
                    onClick={actionCompleteTwoFaSetup}
                    disabled={otp.length !== 6 || loader}
                    isLoading={loader}
                    loadingSize={22}
                    loadingText="PROCESSING"
                />
            </DialogActions>
        </Dialog>
    );
};

const mapStateToProps = (state: any): any => ({
    profileState: state.profileState,
});

const mapDispatchToProps = (dispatch: any): any => ({
    update2FAStatus: (data: any) =>
        dispatch(ProfileActions.update2FAStatus(data)),
});

export default connect(
    mapStateToProps,
    mapDispatchToProps
)(TwoFactorConfirmationDialog);
